import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ModalCommon from "components/ModalCommon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useGetVincularCodigo } from "services/api/AppWeb_EspelhoVendas/get-vincular-codigo";

import { CARGO_CODES } from "constants/permissions";
import { toast } from "react-toastify";
import { RootStateOrAny, useSelector } from "react-redux";
import { AuthState } from "store/ducks/auth/types";
import VincularCodigoModal from "./components/VincularCodigoModal";
import VincularLeadModal from "./components/VincularLeadModal";
import EscolherOpcaoModal from "./components/EscolherOpcaoModal";
import * as Styles from "./styles";

interface UnidadeProps {
  corUnidadeFundo: string;
  corUnidadeTexto: string;
  dataAtivacao: string | null;
  descStatusUnidade: string;
  idUnidade: string;
  metragem: string;
  nomeAndar: string;
  nomeBloco: string;
  nomeUnidade: string;
  obsUnidade: string | null;
  statusUnidade: string;
  toolTip: string;
  valor: string;
  bloqueado: boolean;
  tipologia?: string | null;
  enquadramento?: string | null;
  vagasGaragem?: string | number | null;
}

interface PavimentoProps {
  andar: number;
  unidades: UnidadeProps[];
}

interface ProductSelectProps {
  value: string;
  label: string;
  codigoSap: string;
}

interface SelectProps {
  value: string;
  label: string;
}

interface DataProps {
  bandeira: SelectProps;
  bloco: SelectProps;
  produto: ProductSelectProps;
}

interface Props {
  data: PavimentoProps[];
  headerData: DataProps;
  zoomLevel: number;
  idLead: string | undefined;
  buttonState?: boolean;
  dataResumo: any;
}

const Espelho: React.FC<Props> = (props) => {
  const { codigoCargo2013 } = useSelector<RootStateOrAny, AuthState>(
    (state) => state.user
  );
  const params: any = useParams();
  const queryClient = useQueryClient();
  const [openModalEscolherOpcao, setOpenModalEscolherOpcao] = useState(false);
  const [openModalVincularCodigo, setOpenModalVincularCodigo] = useState(false);
  const [isVincularLeadModalOpen, setIsVincularLeadModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UnidadeProps | null>(null);
  const [leadProducts, setLeadProducts] = useState({
    label: "",
    value: "",
    idLeadCorretor: "",
  });
  const [selectedOption, setSelectedOption] = useState<string>("1");
  const [leadCode, setLeadCode] = useState<string>("");
  const isGerenteCia = codigoCargo2013 === CARGO_CODES.GERENTE_CIA;
  const isCorretor = codigoCargo2013 === CARGO_CODES.CORRETOR;
  const isCoordenadorCia = codigoCargo2013 === CARGO_CODES.COORDENADOR_CIA;

  const {
    data: dataVincularCodigo,
    refetch: refetchVincularCodigo,
    isFetching: isLoadingVincularCodigo,
    isError: isErrorVincularCodigo,
  } = useGetVincularCodigo({
    TipoDeListagem: selectedOption,
    CodigoDoLead: selectedOption === "1" ? leadCode : "",
  });

  const handleOpen = (item: UnidadeProps) => {
    if (item.bloqueado !== true) {
      setSelectedItem(item);
      if (props.idLead && props.dataResumo.simuladorAssociativo === true) {
        setOpenModalEscolherOpcao(true);
      }
      if (props.dataResumo.simuladorAssociativo === false && props.idLead) {
        if (props.idLead !== undefined) {
          window.location.pathname = `lead-detail/${params.idLeadCorretor}/${props.idLead}/repasse-chaves/${item?.idUnidade}`;
        } else {
          window.location.pathname = `lead-detail/${leadProducts.idLeadCorretor}/${leadProducts.value}/repasse-chaves/${item?.idUnidade}`;
        }
      }
      if (!props.idLead) {
        if (isCorretor || isCoordenadorCia) {
          setIsVincularLeadModalOpen(true);
          setLeadProducts({ label: "", value: "", idLeadCorretor: "" });
        } else {
          // Limpar cache dos dados anteriores antes de abrir o modal
          queryClient.removeQueries(["get-vincular-codigo"]);
          setOpenModalVincularCodigo(true);
          setLeadProducts({ label: "", value: "", idLeadCorretor: "" });
          setSelectedOption("1");
          setLeadCode("");
        }
      }
    } else {
      toast.error("Não é possível simular nessa unidade");
    }
  };

  const handleClose = () => {
    setSelectedItem(null);
  };

  const handleCloseVincularCodigo = () => {
    setOpenModalVincularCodigo(false);
    setSelectedOption("1");
    setLeadCode("");
  };

  const formatarVaga = (vagasGaragem: UnidadeProps["vagasGaragem"]) => {
    if (vagasGaragem === null || vagasGaragem === undefined) return null;
    const texto = String(vagasGaragem).trim();
    if (texto === "") return null;

    const quantidade = Number(texto);
    if (Number.isNaN(quantidade)) return texto;
    if (quantidade === 0) return "Sem vaga";
    return quantidade === 1 ? "1 vaga" : `${quantidade} vagas`;
  };

  // O toolTip vem da API no formato "metragem - valor enquadramento ; vaga ; tipologia".
  // Algumas unidades (ex.: vendidas) vem sem o valor, entao o titulo termina
  // no valor quando existir, ou na metragem quando nao.
  const separarToolTip = (toolTip: string | undefined) => {
    const texto = (toolTip ?? "").trim();
    const fimDoTitulo =
      texto.match(/R\$\s*[\d.]+(,\d{2})?/) ?? texto.match(/[\d.,]+\s*m²/);

    if (!fimDoTitulo || fimDoTitulo.index === undefined) {
      return { titulo: texto, detalhes: [] as string[] };
    }

    const corte = fimDoTitulo.index + fimDoTitulo[0].length;
    const titulo = texto.slice(0, corte).replace(/\s+-\s+/, " — ");
    const detalhes = texto
      .slice(corte)
      .split(";")
      .map((parte) => parte.trim())
      .filter(Boolean);

    return { titulo, detalhes };
  };

  const montarToolTip = (item: UnidadeProps) => {
    const { titulo, detalhes: detalhesDoToolTip } = separarToolTip(
      item?.toolTip
    );
    const detalhesDosCampos = [
      item?.enquadramento,
      formatarVaga(item?.vagasGaragem),
      item?.tipologia,
    ].filter(Boolean);
    const detalhes =
      detalhesDoToolTip.length > 0 ? detalhesDoToolTip : detalhesDosCampos;

    return (
      <Styles.TooltipContent>
        <Styles.TooltipTitle>{titulo}</Styles.TooltipTitle>
        {detalhes.length > 0 && (
          <Styles.TooltipDetails>{detalhes.join(" • ")}</Styles.TooltipDetails>
        )}
        {item?.dataAtivacao && (
          <Styles.TooltipDate>{item.dataAtivacao}</Styles.TooltipDate>
        )}
      </Styles.TooltipContent>
    );
  };

  const sortedFloors = [...props.data].sort((a, b) => b.andar - a.andar);

  return (
    <>
      {props.buttonState === true ? (
        <>
          {" "}
          <Styles.Container columns={0}>
            {sortedFloors.map((floor) => (
              <Styles.FloorRow key={floor.andar}>
                <Styles.FloorLabel zoomLevel={props.zoomLevel}>
                  {floor.andar}º
                </Styles.FloorLabel>
                <Styles.UnitGrid columns={props.zoomLevel}>
                  {floor.unidades.map((item) => (
                    <Tooltip
                      style={{ cursor: "pointer" }}
                      title={montarToolTip(item)}
                      placement="top"
                      followCursor
                      arrow
                      componentsProps={Styles.tooltipComponentsProps}
                      key={item.idUnidade}
                    >
                      <Styles.Unity
                        corUnidadeFundo={item.corUnidadeFundo}
                        zoomLevel={props.zoomLevel}
                        onClick={() => handleOpen(item)}
                      >
                        <Styles.UnityText
                          corUnidadeTexto={item.corUnidadeTexto}
                          zoomLevel={props.zoomLevel}
                        >
                          {item.nomeUnidade}
                        </Styles.UnityText>
                        <Styles.UnityText
                          corUnidadeTexto={item.corUnidadeTexto}
                          zoomLevel={props.zoomLevel}
                        >
                          {item.descStatusUnidade}
                        </Styles.UnityText>
                      </Styles.Unity>
                    </Tooltip>
                  ))}
                </Styles.UnitGrid>
              </Styles.FloorRow>
            ))}
          </Styles.Container>
          <ModalCommon
            open={isVincularLeadModalOpen}
            setOpenModal={setIsVincularLeadModalOpen}
            onClose={() => setIsVincularLeadModalOpen(false)}
          >
            <VincularLeadModal
              onClose={() => setIsVincularLeadModalOpen(false)}
              leadProducts={leadProducts}
              setLeadProducts={setLeadProducts}
              setOpenModalEscolherOpcao={setOpenModalEscolherOpcao}
              dataResumo={props.dataResumo}
              selectedItem={selectedItem}
            />
          </ModalCommon>
          <ModalCommon
            open={openModalEscolherOpcao}
            setOpenModal={setOpenModalEscolherOpcao}
            onClose={() => setOpenModalEscolherOpcao(false)}
          >
            <EscolherOpcaoModal
              onClose={() => setOpenModalEscolherOpcao(false)}
              handleOpenProposta={() => {
                setOpenModalEscolherOpcao(false);
                if (props.idLead !== undefined) {
                  window.location.pathname = `lead-detail/${params.idLeadCorretor}/repasse-chaves/${selectedItem?.idUnidade}`;
                } else {
                  window.location.pathname = `lead-detail/${leadProducts.idLeadCorretor}/repasse-chaves/${selectedItem?.idUnidade}`;
                }
              }}
              selectedItem={selectedItem}
              idLead={props.idLead}
              leadProducts={leadProducts}
              dataResumo={props.dataResumo}
              dataVincularCodigo={dataVincularCodigo}
            />
          </ModalCommon>
          <ModalCommon
            open={openModalVincularCodigo}
            setOpenModal={setOpenModalVincularCodigo}
            onClose={handleCloseVincularCodigo}
          >
            <VincularCodigoModal
              onClose={handleCloseVincularCodigo}
              setOpenModalEscolherOpcao={setOpenModalEscolherOpcao}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              leadCode={leadCode}
              setLeadCode={setLeadCode}
              dataResumo={props.dataResumo}
              dataVincularCodigo={dataVincularCodigo}
              refetchVincularCodigo={refetchVincularCodigo}
              isLoadingVincularCodigo={isLoadingVincularCodigo}
              selectedItem={selectedItem}
              isErrorVincularCodigo={isErrorVincularCodigo}
              setLeadProducts={setLeadProducts}
            />
          </ModalCommon>
        </>
      ) : (
        <Styles.ContainerFlex>
          {" "}
          <div style={{ width: "40%" }}>
            {props.dataResumo?.resumos?.resumoBloco ? (
              <Table
                aria-label="simple table"
                sx={{
                  border: "1px solid #D5D5D5",
                  borderRadius: "8px",
                  overflow: "hidden",
                  borderCollapse: "separate",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                      }}
                    >
                      <Styles.TableTextHeader>
                        Resumo por Status (Bloco)
                      </Styles.TableTextHeader>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                      }}
                    >
                      <Styles.TableTextHeader> </Styles.TableTextHeader>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        Total de Unidades
                      </Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.totalUnidade}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>ASSINADA</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.assinada}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>PV</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.pv}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>CRÉDITO</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.credito}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>JURÍDICO</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.juridico}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        AGUARDANDO ASSINATURA
                      </Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.aguardandoAss}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>ESTOQUE</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.estoque}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>BLOQUEADA</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoBloco?.bloqueada}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Styles.UnityText>Carregando...</Styles.UnityText>
            )}
          </div>
          <div style={{ width: "40%" }}>
            {props.dataResumo?.resumos?.resumoEmpreendimento ? (
              <Table
                aria-label="simple table"
                sx={{
                  border: "1px solid #D5D5D5",
                  borderRadius: "8px",
                  overflow: "hidden",
                  borderCollapse: "separate",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                      }}
                    >
                      <Styles.TableTextHeader>
                        Resumo por Status (Empreendimento)
                      </Styles.TableTextHeader>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "12px 16px",
                      }}
                    >
                      <Styles.TableTextHeader> </Styles.TableTextHeader>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        TOTAL DE UNIDADES
                      </Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {
                          props.dataResumo?.resumos?.resumoEmpreendimento
                            ?.totalUnidade
                        }
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>ASSINADA</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {
                          props.dataResumo?.resumos?.resumoEmpreendimento
                            ?.assinada
                        }
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>PV</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {props.dataResumo?.resumos?.resumoEmpreendimento?.pv}
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>CRÉDITO</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {
                          props.dataResumo?.resumos?.resumoEmpreendimento
                            ?.credito
                        }
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>JURÍDICO</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {
                          props.dataResumo?.resumos?.resumoEmpreendimento
                            ?.juridico
                        }
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        AGUARDANDO ASSINATURA
                      </Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {
                          props.dataResumo?.resumos?.resumoEmpreendimento
                            ?.aguardandoAss
                        }
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>ESTOQUE</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {
                          props.dataResumo?.resumos?.resumoEmpreendimento
                            ?.estoque
                        }
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>BLOQUEADA</Styles.TableTextBody>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px 16px",
                      }}
                    >
                      <Styles.TableTextBody>
                        {
                          props.dataResumo?.resumos?.resumoEmpreendimento
                            ?.bloqueada
                        }
                      </Styles.TableTextBody>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Styles.UnityText>Carregando...</Styles.UnityText>
            )}
          </div>
        </Styles.ContainerFlex>
      )}
    </>
  );
};

export default Espelho;
